import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { CTABanner } from "@/components/sections/cta-banner";
import {
  BookOpen, FileText, GraduationCap, BarChart3, FileBarChart, ScrollText, ArrowRight,
} from "lucide-react";

export const Route = createFileRoute("/resources/")({
  component: ResourcesIndex,
  head: () => ({
    meta: [
      { title: "Resources — CoderLity" },
      { name: "description", content: "Blog, guides, tutorials, industry reports and whitepapers from the CoderLity team." },
      { property: "og:title", content: "Resources — CoderLity" },
      { property: "og:description", content: "Blog, guides, tutorials, industry reports and whitepapers from the CoderLity team." },
      { property: "og:url", content: "/resources" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/resources" }],
  }),
});

type Item = { slug: string; title: string; desc: string; icon: typeof BookOpen };
const learn: Item[] = [
  { slug: "blog", title: "Blog", desc: "Explore blog", icon: BookOpen },
  { slug: "guides", title: "Guides", desc: "Explore guides", icon: FileText },
  { slug: "tutorials", title: "Tutorials", desc: "Explore tutorials", icon: GraduationCap },
];
const reports: Item[] = [
  { slug: "industry-reports", title: "Industry Reports", desc: "Explore industry reports", icon: BarChart3 },
  { slug: "whitepapers", title: "Whitepapers", desc: "Explore whitepapers", icon: ScrollText },
];

function Group({ title, items }: { title: string; items: Item[] }) {
  return (
    <section className="py-12">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <h2 className="text-3xl font-bold tracking-tight">{title}</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <Link
                key={it.slug}
                to="/resources/$child"
                params={{ child: it.slug }}
                className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-brand text-white shadow-glow">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-semibold">{it.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground inline-flex items-center gap-1">
                  {it.desc} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </p>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ResourcesIndex() {
  return (
    <PageShell>
      <PageHero
        eyebrow="Resources"
        title="Learn from the people shipping it"
        sub="Practical writing from the CoderLity team — blog posts, in-depth guides, tutorials, industry reports and whitepapers."
      />
      <Group title="Learn" items={learn} />
      <Group title="Reports" items={reports} />
      <CTABanner />
    </PageShell>
  );
}
